import { DocumentTypeEnum, FileExtEnum, type InvoiceInterface, type OcrResult } from "@finway-group/shared/lib/models"
import { removeQueryString } from "@finway-group/shared/lib/utils"
import { Tabs } from "antd"
import React, { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { appendPendingGeneratedReceiptTab, shouldOpenPendingGeneratedReceiptTab } from "~Components/documentTabManager.utils"
import { EInvoiceTag } from "~Components/eInvoiceTag"
import { getInvoiceXMLRenderer } from "~Components/layout/invoice.placeholder"
import { Loading } from "~Components/loading"
import { UploadFileDraggerContent } from "~Components/upload/UploadFileDraggerContent"
import { UploadFilesDragger, type UploadFilesDraggerInterface } from "~Components/upload/UploadFilesDragger"
import { FileService } from "~Shared/services/file.service"
import { isFileExtensionMatching } from "~Shared/utils/file.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { lazily } from "~Shared/utils/react.utils"

const { FileViewerCached } = lazily(() => import("~Components/fileViewer/FileViewerCached"))

interface DocumentTabManagerProps {
    invoices?: Array<InvoiceInterface>
    deliveryNote?: string
    doFileUpload: UploadFilesDraggerInterface["doFileUpload"]
    onRemoveTab: (documentType: DocumentTypeEnum, targetKey: string | React.MouseEvent | React.KeyboardEvent) => void
    documentType: DocumentTypeEnum
    label: {
        buttonLabel: string
        draggerDescription: string
    }
    onChangeInvoicesActiveKey?: (activeInvoiceTabKey: number) => void
    onInboxSelected?: (event: any) => void
    isUploading?: boolean
    readOnly?: boolean
    showPaidStamp?: boolean
    /** Only allowed once the expense is no longer a self-issued receipt, because the receipt cannot exist without its document. */
    isGeneratedReceiptRemovable?: boolean
    /** While the generated receipt file is still missing, show the upload animation. Existing documents need an extra tab for that. */
    isAwaitingGeneratedReceipt?: boolean
}

interface InvoiceTabPagesProps {
    newDoc: boolean
    title: string
    filePath: string
    isEInvoice: boolean
    /** A generated self-issued receipt document belongs to its expense, so its tab is only closable while the flag is off. */
    isSelfIssued?: boolean
    /** Placeholder opened while the generated receipt is created next to documents that are already attached. */
    isPendingGeneratedReceipt?: boolean
    fileData?: Uint8Array
    ocrResults?: OcrResult
}

export const DocumentTabManager = ({
    invoices,
    deliveryNote,
    doFileUpload,
    onRemoveTab,
    documentType,
    label,
    onChangeInvoicesActiveKey,
    onInboxSelected,
    isUploading,
    readOnly = false,
    showPaidStamp = false,
    isGeneratedReceiptRemovable = false,
    isAwaitingGeneratedReceipt = false,
}: DocumentTabManagerProps) => {
    const { t } = useTranslation()
    const [invoiceTabPages, setInvoiceTabPages] = useState<Array<InvoiceTabPagesProps>>([])
    const [activeInvoiceTabKey, setActiveInvoiceTabKey] = useStateIfMounted(0)
    const showPendingGeneratedReceiptTab = shouldOpenPendingGeneratedReceiptTab({
        isAwaitingGeneratedReceipt,
        attachedDocumentCount: invoices?.length ?? 0,
    })
    const switchTypeCondition =
        documentType === DocumentTypeEnum.INVOICE || documentType === DocumentTypeEnum.OFFER ? invoices?.length === 0 : !deliveryNote || deliveryNote === undefined

    const getTabTitle = (invoiceTabPage: InvoiceTabPagesProps, index: number) => {
        const clientIndex = index + 1

        if (!invoiceTabPage.newDoc && !invoiceTabPage.title) {
            switch (documentType) {
                case DocumentTypeEnum.INVOICE:
                    return `${t("label:expense_document.invoice")} ${clientIndex}`
                case DocumentTypeEnum.OFFER:
                    return `${t("label:expense_document.offer")} ${clientIndex}`
                default:
                    return invoiceTabPage.title
            }
        } else {
            return invoiceTabPage.title
        }
    }

    useEffect(() => {
        const invoiceTabPagesTmp: Array<InvoiceTabPagesProps> = invoiceTabPages
            .filter((i: InvoiceTabPagesProps) => invoices?.find((invoice: InvoiceInterface) => i.filePath === invoice?.url))
            .map((i) => {
                const invoice = invoices?.find((invoice) => i.filePath === invoice?.url)
                return {
                    ...i,
                    isEInvoice: invoice?.isEInvoice ?? false,
                    isSelfIssued: invoice?.isSelfIssued ?? false,
                    ocrResults: invoice?.invoiceData,
                }
            })

        invoices?.forEach((invoice) => {
            const docUrl = invoice?.url ?? invoice
            const title = invoice?.fileName ?? ""
            if (!invoiceTabPagesTmp.some((e: InvoiceTabPagesProps) => e.filePath === docUrl)) {
                invoiceTabPagesTmp.push({
                    newDoc: false,
                    title,
                    filePath: docUrl,
                    isEInvoice: invoice.isEInvoice,
                    isSelfIssued: invoice.isSelfIssued,
                    ocrResults: invoice.invoiceData,
                })
            }
        })

        // invoiceTabPages is only read to keep tabs already on screen. Depending on it would retrigger this effect after every write.
        setInvoiceTabPages(
            appendPendingGeneratedReceiptTab({
                tabs: invoiceTabPagesTmp,
                shouldOpenPendingTab: showPendingGeneratedReceiptTab,
                pendingTab: {
                    newDoc: true,
                    filePath: "",
                    title: t("label:expense_document.new_document"),
                    isEInvoice: false,
                    isPendingGeneratedReceipt: true,
                },
            }),
        )
    }, [JSON.stringify(invoices), showPendingGeneratedReceiptTab])

    useEffect(() => {
        setActiveInvoiceTabKey(Math.max(invoiceTabPages.length - 1, 0))
    }, [invoiceTabPages])

    useEffect(() => {
        onChangeInvoicesActiveKey && onChangeInvoicesActiveKey(activeInvoiceTabKey)
    }, [activeInvoiceTabKey])

    const onChange = (activeInvoiceTabKey: string) => {
        setActiveInvoiceTabKey(parseInt(activeInvoiceTabKey))
    }

    const onActionTab = (documentType: DocumentTypeEnum, targetKey: string | React.MouseEvent | React.KeyboardEvent, action: "remove" | "add") => {
        if (action === "remove") {
            const index = Number(targetKey)
            if (invoiceTabPages[index]?.isPendingGeneratedReceipt) {
                return
            }
            if (invoiceTabPages[index]?.newDoc) {
                setInvoiceTabPages(invoiceTabPages.slice(0, index))
            } else {
                onRemoveTab(documentType, targetKey)
            }
        } else if (action === "add") {
            if (showPendingGeneratedReceiptTab) {
                return
            }
            setInvoiceTabPages([...invoiceTabPages, { newDoc: true, filePath: "", title: t("label:expense_document.new_document"), isEInvoice: false }])
        }
    }

    return (
        <>
            {switchTypeCondition ? (
                <UploadFilesDragger doFileUpload={doFileUpload} documentType={documentType} disabled={isUploading || isAwaitingGeneratedReceipt || readOnly}>
                    <UploadFileDraggerContent label={label} onInboxSelected={onInboxSelected} isUploading={isUploading || isAwaitingGeneratedReceipt} readOnly={readOnly} />
                </UploadFilesDragger>
            ) : (
                <>
                    <Tabs
                        type={readOnly ? "card" : "editable-card"}
                        hideAdd={documentType === DocumentTypeEnum.DELIVERY_NOTE || readOnly || showPendingGeneratedReceiptTab}
                        onChange={onChange}
                        activeKey={documentType === DocumentTypeEnum.INVOICE || documentType === DocumentTypeEnum.OFFER ? `${activeInvoiceTabKey}` : "deliveryNote"}
                        onEdit={(targetKey, action) => onActionTab(documentType, targetKey, action)}
                    >
                        {documentType === DocumentTypeEnum.INVOICE || documentType === DocumentTypeEnum.OFFER
                            ? invoiceTabPages.map((tabPage: InvoiceTabPagesProps, index: number) => {
                                  const isXmlEInvoice = tabPage.isEInvoice && tabPage.filePath.includes("xml")
                                  return (
                                      <Tabs.TabPane
                                          key={index}
                                          tab={getTabTitle(tabPage, index)}
                                          closable={!tabPage.isPendingGeneratedReceipt && (!tabPage.isSelfIssued || isGeneratedReceiptRemovable)}
                                          forceRender
                                      >
                                          {tabPage.newDoc ? (
                                              <UploadFilesDragger
                                                  doFileUpload={doFileUpload}
                                                  documentType={documentType}
                                                  disabled={isUploading || isAwaitingGeneratedReceipt || readOnly}
                                              >
                                                  <UploadFileDraggerContent
                                                      label={label}
                                                      isUploading={isUploading || isAwaitingGeneratedReceipt}
                                                      onInboxSelected={onInboxSelected}
                                                      readOnly={readOnly}
                                                  />
                                              </UploadFilesDragger>
                                          ) : (
                                              <>
                                                  <Suspense fallback={<Loading />}>
                                                      <FileViewerCached
                                                          key={removeQueryString(tabPage.filePath)}
                                                          filePath={tabPage.filePath}
                                                          showPaidStamp={showPaidStamp}
                                                          isXmlEInvoice={isXmlEInvoice}
                                                          pdfHeight={isXmlEInvoice ? "auto" : undefined}
                                                          RightSideToolbar={tabPage.isEInvoice ? <EInvoiceTag /> : null}
                                                          renderError={getInvoiceXMLRenderer({
                                                              fileName: tabPage.filePath,
                                                              isEInvoice: tabPage.isEInvoice,
                                                              isShowingPaidStamp: showPaidStamp,
                                                              ocrResults: tabPage.ocrResults,
                                                          })}
                                                          onUnsupportedFormatDownload={() => FileService.downloadFileAsSignedUrl(tabPage.filePath)}
                                                      />
                                                  </Suspense>
                                              </>
                                          )}
                                      </Tabs.TabPane>
                                  )
                              })
                            : deliveryNote && (
                                  <Tabs.TabPane closable tab={t("label:expense_document.delivery_note")} key="deliveryNote" forceRender>
                                      {isFileExtensionMatching(deliveryNote, FileExtEnum.PDF) && (
                                          <Suspense fallback={<Loading />}>
                                              <FileViewerCached filePath={deliveryNote} showPaidStamp={showPaidStamp} />
                                          </Suspense>
                                      )}
                                  </Tabs.TabPane>
                              )}
                    </Tabs>
                </>
            )}
        </>
    )
}
